import React from 'react';
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import type { DefaultOption } from 'src/components/molecules/DropdownSelect/components/DropdownOptions';
import { DATA_GRID_COLUMN_TYPES } from 'src/types/formQuestionnaire';

export const INPUT_PLACEHOLDER = 'Add your answer here';

export const renderInputField = ({
  type = DATA_GRID_COLUMN_TYPES.TEXT,
  value,
  onChange,
  placeholder = INPUT_PLACEHOLDER,
  testId,
  min,
  max,
  step,
  error,
  helperText,
}: {
  type?:
    | typeof DATA_GRID_COLUMN_TYPES.TEXT
    | typeof DATA_GRID_COLUMN_TYPES.NUMBER;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  testId: string;
  min?: number;
  max?: number;
  step?: number;
  error?: boolean;
  helperText?: string;
}) => (
  <TextField
    type={type}
    fullWidth
    placeholder={placeholder}
    value={value ?? ''}
    onChange={onChange}
    error={error}
    helperText={helperText}
    slotProps={{ htmlInput: { 'data-testid': testId, min, max, step } }}
  />
);

export const renderDropdownField = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  isMulti = false,
  testId,
}: {
  options: { label: string; value: string }[];
  value: DefaultOption | DefaultOption[] | null;
  onChange: (value: unknown) => void;
  placeholder?: string;
  isMulti?: boolean;
  testId: string;
}) => {
  const selectValue = isMulti
    ? Array.isArray(value)
      ? value.map((v) => String(v.value))
      : []
    : value && !Array.isArray(value)
      ? String(value.value)
      : '';

  const handleChange = (e: SelectChangeEvent<string | string[]>) => {
    const raw = e.target.value;
    if (isMulti) {
      const selected = (raw as string[]).map(
        (v) =>
          options.find((o) => String(o.value) === v) ?? { label: v, value: v },
      );
      onChange(selected);
    } else {
      const selected = options.find((o) => String(o.value) === raw) ?? null;
      onChange(selected);
    }
  };

  return (
    <Select
      fullWidth
      multiple={isMulti}
      value={selectValue}
      onChange={handleChange}
      displayEmpty
      renderValue={(selected) => {
        if (
          !selected ||
          (Array.isArray(selected) && selected.length === 0) ||
          selected === ''
        ) {
          return <span style={{ opacity: 0.5 }}>{placeholder}</span>;
        }
        if (isMulti) {
          return (selected as string[])
            .map((v) => options.find((o) => String(o.value) === v)?.label ?? v)
            .join(', ');
        }
        return String(
          options.find((o) => String(o.value) === selected)?.label ??
            (selected as string),
        );
      }}
      MenuProps={{ sx: { zIndex: 4001 } }}
      SelectDisplayProps={
        { 'data-testid': testId } as React.HTMLAttributes<HTMLDivElement>
      }
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};
